import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../services/firebase';

export default function DeleteAccountPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync email if query param changes (e.g. deep-link navigation)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) setEmail(decodeURIComponent(emailParam));
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Re-authenticate the user
      await signInWithEmailAndPassword(auth, email, password);

      // Call the Cloud Function while the user is authenticated
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      await deleteUserAccount();

      // Sign out the (now-deleted) session
      await auth.signOut();

      setSuccess(true);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      if (
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found' ||
        code === 'auth/invalid-credential'
      ) {
        setError(t('error_wrong_credentials'));
      } else if (code === 'auth/too-many-requests') {
        setError(t('error_too_many_requests'));
      } else {
        setError(t('error_generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page page-center" style={{ minHeight: '100vh', background: '#f4f6fa' }}>
      <div className="container-tight py-4">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="mb-3">
            <span style={{ fontSize: 48 }}>🌲</span>
          </div>
          <h2 className="h2 fw-bold text-danger">{t('page_title')}</h2>
          <p className="text-muted">{t('page_subtitle')}</p>
        </div>

        {success ? (
          /* ── Success state ── */
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="mb-3" style={{ fontSize: 48 }}>
                ✅
              </div>
              <h3 className="card-title text-success">{t('success_title')}</h3>
              <p className="text-muted">{t('success_message')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Warning card ── */}
            <div className="card border-danger mb-4">
              <div className="card-header bg-danger-subtle">
                <h3 className="card-title text-danger mb-0">⚠️ {t('warning_title')}</h3>
              </div>
              <div className="card-body">
                <ul className="list-unstyled mb-0">
                  {(
                    [
                      'warning_forum_posts',
                      'warning_quote_requests',
                      'warning_statistics',
                      'warning_favorites',
                      'warning_account',
                    ] as const
                  ).map(key => (
                    <li key={key} className="mb-2 d-flex gap-2">
                      <span className="text-danger mt-1">✕</span>
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ── Auth form ── */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title mb-0">{t('form_title')}</h3>
              </div>
              <div className="card-body">
                <p className="text-muted mb-3">{t('form_subtitle')}</p>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="email">
                      {t('email_label')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="form-control"
                      placeholder={t('email_placeholder')}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label" htmlFor="password">
                      {t('password_label')}
                    </label>
                    <input
                      id="password"
                      type="password"
                      className="form-control"
                      placeholder={t('password_placeholder')}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>

                  <button type="submit" className="btn btn-danger w-100" disabled={loading}>
                    {loading ? t('submitting') : t('submit_button')}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
